import { useState } from 'react';
import { api, type Approval } from '../lib/api';
import { Card, Empty, ErrorNote, Field, Loading, Modal, PageHead, Pill, useResource, useToast } from '../components/ui';
import { dateTime } from '../lib/format';
import '../styles/controls.css';

/**
 * Changes held for a second person. One table: what was asked, who asked,
 * when, the status, and the one next step for this viewer.
 */

const STATUS_LABEL: Record<Approval['status'], string> = {
  pending: 'Pending', approved: 'Approved', rejected: 'Rejected', cancelled: 'Withdrawn', expired: 'Expired', failed: 'Failed',
};
const STATUS_TONE: Record<Approval['status'], 'ok' | 'warn' | 'bad' | undefined> = {
  pending: 'warn', approved: 'ok', rejected: 'bad', cancelled: undefined, expired: undefined, failed: 'bad',
};

type Tab = 'pending' | 'decided';

export default function Approvals() {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('pending');
  const [reload, setReload] = useState(0);
  const [deciding, setDeciding] = useState<{ approval: Approval; verb: 'approve' | 'reject' } | null>(null);
  const data = useResource<{ approvals: Approval[]; pending: number }>(
    () => api.get(`/v1/approvals?status=${tab}`), [tab, reload],
  );
  const rows = data.data?.approvals ?? [];

  async function withdraw(a: Approval) {
    if (!window.confirm('Withdraw this request?')) return;
    try {
      await api.post(`/v1/approvals/${a.id}/cancel`);
      toast('Withdrawn.', 'ok');
      setReload((n) => n + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <>
      <PageHead title="Approvals" description="Sensitive changes wait here until a second person approves them. The person who asked cannot approve." />
      <div className="tabs">
        <button className={tab === 'pending' ? 'active' : ''} onClick={() => setTab('pending')}>
          Pending{data.data?.pending ? <span className="tab-count">{data.data.pending}</span> : null}
        </button>
        <button className={tab === 'decided' ? 'active' : ''} onClick={() => setTab('decided')}>Decided</button>
      </div>
      <ErrorNote error={data.error} />
      <Card>
        {data.loading && !data.data && <Loading what="Loading approvals" />}
        {data.data && rows.length === 0 && <Empty>{tab === 'pending' ? 'Nothing waiting.' : 'No decisions yet.'}</Empty>}
        {rows.length > 0 && (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr><th>Change</th><th>Asked by</th><th>Asked</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div className="approval-row-summary">{a.summary}</div>
                      <div className="small muted">{a.actionLabel}{a.reason ? ` · ${a.reason}` : ''}</div>
                    </td>
                    <td className="small">{a.mine ? 'You' : a.requestedBy}</td>
                    <td className="small nowrap">{dateTime(a.requestedAt)}</td>
                    <td>
                      <Pill tone={STATUS_TONE[a.status]}>{STATUS_LABEL[a.status]}</Pill>
                      {a.decidedBy && a.status !== 'cancelled' && (
                        <div className="small muted">{a.decidedBy}{a.decidedAt ? `, ${dateTime(a.decidedAt)}` : ''}</div>
                      )}
                      {a.decisionReason && <div className="small muted">{a.decisionReason}</div>}
                      {a.status === 'failed' && a.error && <div className="small" style={{ color: 'var(--bad)' }}>{a.error}</div>}
                      {a.status === 'pending' && <div className="small muted">Expires {dateTime(a.expiresAt)}</div>}
                    </td>
                    <td className="right nowrap">
                      {a.canDecide && (
                        <>
                          <button className="small" onClick={() => setDeciding({ approval: a, verb: 'reject' })}>Reject</button>{' '}
                          <button className="small primary" onClick={() => setDeciding({ approval: a, verb: 'approve' })}>Approve</button>
                        </>
                      )}
                      {a.mine && a.status === 'pending' && <button className="small" onClick={() => withdraw(a)}>Withdraw</button>}
                      {!a.canDecide && !a.mine && a.status === 'pending' && <span className="small muted">Awaiting a person who can approve</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {deciding && (
        <DecideDialog
          approval={deciding.approval}
          verb={deciding.verb}
          onClose={() => setDeciding(null)}
          onDone={() => { setDeciding(null); setReload((n) => n + 1); }}
        />
      )}
    </>
  );
}

function DecideDialog({ approval: a, verb, onClose, onDone }: {
  approval: Approval; verb: 'approve' | 'reject'; onClose: () => void; onDone: () => void;
}) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const approving = verb === 'approve';

  async function submit() {
    setBusy(true); setError(null);
    try {
      const res = await api.post<{ approval: Approval }>(`/v1/approvals/${a.id}/${verb}`, approving ? { note: text } : { reason: text });
      if (res.approval.status === 'failed') toast(`Approved, but the change could not be made: ${res.approval.error ?? 'unknown error'}`, 'bad');
      else toast(approving ? 'Approved. The change is made.' : 'Rejected.', 'ok');
      onDone();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title={approving ? 'Approve change' : 'Reject change'}
      description={approving ? 'The change is made as soon as you approve, in the name of the person who asked.' : undefined}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className={approving ? 'primary' : 'danger'} onClick={submit} disabled={busy || (!approving && text.trim().length < 5)}>
            {busy ? 'Saving…' : approving ? 'Approve' : 'Reject'}
          </button>
        </>
      }
    >
      <dl className="kv">
        <dt>Change</dt><dd>{a.summary}</dd>
        <dt>Asked by</dt><dd>{a.requestedBy}, {dateTime(a.requestedAt)}</dd>
        {a.reason && <><dt>Their reason</dt><dd>{a.reason}</dd></>}
      </dl>
      <Field label={approving ? 'Note (optional)' : 'Reason'} help={approving ? undefined : 'Required. The person who asked sees it.'}>
        <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} />
      </Field>
      <ErrorNote error={error} />
    </Modal>
  );
}
